/**
 * Who is signed in, and what their role lets them do.
 *
 * The server decides all of this: these helpers only let a screen avoid
 * offering an action that would come back 403. Hiding a button is a courtesy,
 * never a control.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api, primeCsrf } from "@/api/client"
import type { components } from "@/api/schema"
import { roleDisplayName } from "@/lib/instance"

export type Me = components["schemas"]["MeOut"]

export const ADMINISTRATOR = "Administrator"
export const COORDINATOR = "Coordinator"
export const VIEWER = "Viewer"

export const sessionKey = ["session", "me"] as const

export function useSession() {
  const query = useQuery({
    queryKey: sessionKey,
    queryFn: async () => {
      const { data } = await api.GET("/api/auth/me", {})
      return data ?? null
    },
    // A 401 is the answer "nobody is signed in", not a transient failure.
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const roles = query.data?.roles ?? []
  // The server's own rule, mirrored: `require_role` lets a superuser past
  // every check. Leaving it out here hides every action from the bootstrap
  // account, which holds no group and is the only account a fresh instance
  // has, so the product looks unbuilt to the first person who opens it.
  const isSuperuser = query.data?.is_superuser ?? false
  const isAdministrator = isSuperuser || roles.includes(ADMINISTRATOR)
  const isCoordinator = roles.includes(COORDINATOR)

  return {
    ...query,
    me: query.data ?? null,
    roles,
    isSuperuser,
    isAdministrator,
    /** May capture and edit. Viewer may not. */
    canWrite: isAdministrator || isCoordinator,
    /** Sign-off is Administrator-only (decision 5). */
    canSignOff: isAdministrator,
    /** Deleting is a writer action; only signed-off data is reserved. */
    canDelete: isAdministrator || isCoordinator,
    canManageUsers: isAdministrator,
  }
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (credentials: { username: string; password: string }) => {
      // Django needs the CSRF cookie before it will accept the POST.
      await primeCsrf()
      const { data } = await api.POST("/api/auth/login", { body: credentials })
      return data ?? null
    },
    onSuccess: (me) => {
      queryClient.setQueryData(sessionKey, me)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.POST("/api/auth/logout", {})
    },
    onSuccess: () => {
      // Everything cached belonged to the person who just left.
      queryClient.clear()
    },
  })
}

export function displayName(me: Me | null) {
  if (!me) return ""
  const full = `${me.first_name} ${me.last_name}`.trim()
  return full || me.username
}

/**
 * The top bar's identity line: who is signed in, and as what.
 *
 * Composed here rather than in the route file because a TanStack route module
 * owns its exports, so anything written inside one cannot be tested - the same
 * reason `nav.ts` exists. The roles go through the instance's own vocabulary
 * (R8): the server hands back the group identifiers, and this is the one
 * surface that renders whatever set it is given, so it is the one that would
 * otherwise leak `Coordinator` to a client who calls that role Manager.
 */
export function sessionLabel(me: Me | null) {
  if (!me) return ""
  const name = displayName(me)
  const roles = me.roles.map(roleDisplayName).filter(Boolean).join(", ")
  // An account holding no group (the bootstrap superuser) gets its name and
  // no dangling separator.
  return roles ? `${name} · ${roles}` : name
}
